import { defineStore } from 'pinia'
import { api, unAuthApi } from 'boot/receiver/axios'
import axios from 'axios'
import { i18n } from 'boot/common/i18n'

import type {
  TotpSetupRes,
  TwoFactorBackupCodes,
  TwoFactorConfig,
  TwoFactorDisplay,
  TwoFactorRes,
  TwoFactorTypes,
} from 'types/common/account/twoFactor'
import type { PromiseReturn, SuccessApiRes } from 'types/common/api'
import type { CreateTokenWithCodeRes } from 'types/receiver/account/auth'
import type { ErrorRes } from 'types/common/error/error'
import type { CodeMessage } from 'types/common/commonTypes'

import { useErrorStore } from 'stores/common/error'

import checkSudoCode from 'composables/receiver/auth/checkSudoCode'
import { useCookieHelpers } from 'src/composables/common/auth/useCookieHelpers'

export const useTwoFactorStore = defineStore('twoFactor', {
  state: () => ({
    twoFactorConfig: null as null | TwoFactorConfig,
  }),

  getters: {
    twoFactorDisplay(state) {
      const display = { ...state.twoFactorConfig }
      delete display.active
      delete display.enabled

      return display as TwoFactorDisplay
    },
  },

  actions: {
    // GENERAL
    async fetchConfig() {
      if (!this.twoFactorConfig) {
        try {
          const res = await api.get('/account/two-factor/status')

          const data: TwoFactorRes = res.data

          if (data && data.success) {
            this.twoFactorConfig = data.payload
          }
        } catch (error) {
          useErrorStore().updateBackErrors(error)
          return false
        }
      }
    },

    // Setup Challenge
    async setupChallenge<T>(body: T, type: TwoFactorTypes) {
      try {
        const res = await api.post(`/account/two-factor/${type}/enroll`, body)

        const data = res.data

        if (data && data.success) {
          if (this.twoFactorConfig && res.status === 200) {
            this.twoFactorConfig[type] = data.payload
          }
          return { success: true, status: res.status }
        } else
          return {
            success: false,
            status: res.status,
          }
      } catch (error) {
        useErrorStore().updateBackErrors(error, `${type}2FAError`)
        checkSudoCode(error)
        let status = 400
        if (axios.isAxiosError(error) && error.response?.status === 429) {
          status = error.response?.status || 400
        }
        return {
          success: false,
          status: status,
        }
      }
    },
    // Verify Challenge
    async verifyChallenge<T>(
      body: T,
      type: string
    ): PromiseReturn<{ success: true }> {
      try {
        const res = await api.put(`/account/two-factor/${type}/verify`, body)

        const data: TwoFactorRes = res.data

        if (data && data.success) {
          this.twoFactorConfig = data.payload
          return { success: true }
        } else return { success: false, error: '' }
      } catch (error) {
        return { success: false, error }
      }
    },
    // Remove Challenge
    async removeChallenge(sudo_code: string, type: string) {
      try {
        const res = await api.post(`/account/two-factor/${type}/erase`, {
          sudo_code,
        })
        const data: TwoFactorRes = res.data
        if (data && data.success) {
          this.twoFactorConfig = data.payload

          return true
        }
        return false
      } catch (error) {
        useErrorStore().updateBackErrors(error, 'twoFactorError')
        checkSudoCode(error)
        return false
      }
    },
    // Ask challenge
    async askChallenge(challenge_code: string, type: TwoFactorTypes) {
      try {
        const res = await unAuthApi.post(
          `/account/two-factor/${type}/challenge`,
          {
            challenge_code,
          }
        )

        if (res.data.success) {
          return true
        } else return false
      } catch (error) {
        useErrorStore().updateBackErrors(error)
        return false
      }
    },
    // Complete Challenge
    async completeChallenge<T>(
      body: T,
      type: string,
      manageError?: (error: unknown) => void
    ): PromiseReturn<{ success: true }> {
      try {
        const res = await api.post(
          `/account/two-factor/${type}/authenticate`,
          body
        )

        const data: CreateTokenWithCodeRes = res.data
        if (data.success) {
          const cookieHelpers = useCookieHelpers()
          cookieHelpers.setAccessToken(data.payload.access_token)
          cookieHelpers.setRefreshToken(data.payload.refresh_token)
          return { success: true }
        } else return { success: false, error: '' }
      } catch (error) {
        if (manageError) {
          manageError(error)
        } else if (axios.isAxiosError(error)) {
          useErrorStore().updateBackErrors(error)
          if (error.response?.status === 401) {
            useErrorStore().backErrors.digits =
              i18n.global.t('error.otp.Invalid')
          }
        }
        return { success: false, error }
      }
    },
    // SMS
    async setupSmsChallenge(phone: string, sudo_code: string) {
      return this.setupChallenge({ phone, sudo_code }, 'sms')
    },

    async verifyPhone(code: string): PromiseReturn<{ success: true }> {
      return this.verifyChallenge({ code }, 'sms')
    },

    async removeSmsChallenge(sudo_code: string) {
      return this.removeChallenge(sudo_code, 'sms')
    },

    // Login

    async askSmsChallenge(challenge_code: string) {
      return this.askChallenge(challenge_code, 'sms')
    },

    async completeAuthBySms(digits: string, challenge_code: string) {
      return this.completeChallenge({ digits, challenge_code }, 'sms')
    },

    // EMAIL
    async setupEmailChallenge(email: string, sudo_code: string) {
      return this.setupChallenge({ email, sudo_code }, 'email')
    },

    async verifyEmail(code: string): PromiseReturn<{ success: true }> {
      return this.verifyChallenge({ code }, 'email')
    },

    async removeEmailChallenge(sudo_code: string) {
      return this.removeChallenge(sudo_code, 'email')
    },

    // Login
    async askEmailChallenge(challenge_code: string) {
      return this.askChallenge(challenge_code, 'email')
    },

    async completeAuthByEmail(digits: string, challenge_code: string) {
      return this.completeChallenge({ digits, challenge_code }, 'email')
    },

    // TOTP

    async setupTotpChallenge(sudo_code: string) {
      try {
        const res = await api.post('/account/two-factor/totp/enroll', {
          sudo_code,
        })

        const data: TotpSetupRes = res.data

        if (data && data.success) {
          return data.payload
        } else return false
      } catch (error) {
        useErrorStore().updateBackErrors(error, 'totpError')
        checkSudoCode(error)
        return false
      }
    },

    async verifyTotp(
      code: string,
      enrollment_code: string
    ): PromiseReturn<{ success: true }> {
      return this.verifyChallenge({ code, enrollment_code }, 'totp')
    },

    async removeTotpChallenge(sudo_code: string) {
      return this.removeChallenge(sudo_code, 'totp')
    },

    // Login
    async completeAuthByTotp(digits: string, challenge_code: string) {
      return this.completeChallenge({ digits, challenge_code }, 'totp')
    },

    // BACKUP CODE

    async setupBackupCode(sudo_code: string) {
      try {
        const res = await api.post('/account/two-factor/backup-codes/enroll', {
          sudo_code,
        })

        const data: SuccessApiRes<TwoFactorBackupCodes> = res.data

        if (data && data.success && this.twoFactorConfig) {
          this.twoFactorConfig.backup_codes = data.payload
          return data.payload.codes as string[]
        } else return false
      } catch (error) {
        checkSudoCode(error)
        return false
      }
    },

    async removeBackupChallenge(sudo_code: string) {
      return this.removeChallenge(sudo_code, 'backup-codes')
    },

    // Login
    async completeAuthByBackupCode(
      backup_code: string,
      challenge_code: string
    ) {
      return this.completeChallenge(
        { backup_code, challenge_code },
        'backup-codes',
        (error: unknown) => {
          const errorStore = useErrorStore()
          //  Manage 409 errors
          errorStore.updateBackErrors(error)
          // Specific errors messages
          if (axios.isAxiosError(error)) {
            if (
              error.response?.status === 404 ||
              error.response?.status === 401
            ) {
              const res: ErrorRes<CodeMessage> = error.response?.data
              if (
                res.payload.code ===
                'exception.two_factor.backup_code_already_used'
              ) {
                errorStore.backErrors.backup_code = res.payload.message
              } else if (
                res.payload.code === 'exception.security.bad_credentials'
              ) {
                errorStore.backErrors.backup_code = i18n.global.t(
                  'account.signIn.twoFactor.error.backup_codes.invalid'
                )
              }
            }
          }
        }
      )
    },
  },
})
